import React, { useState } from "react";
import { Sparkles, Clock, Users, CircleDollarSign, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import StorefrontHeader from "../components/StorefrontHeader";
import useEmbedResize from "../hooks/useEmbedResize";

/**
 * The Cut — public brand application form.
 * Reuses the Launchpad storefront design system (cream + red accent, Inter,
 * lp-card + lp-btn-primary classes) so it feels native when arrived at from
 * /storefront.
 */
const INITIAL = {
  brand_name: "", founder_name: "", email: "", instagram: "", website: "",
  followers: "", product_name: "", category: "", retail_price: "",
  brand_story: "", online_store: "", fulfilment: "", lead_time: "",
  promo_plan: "", anything_else: "",
};
const REQUIRED = [
  "brand_name", "founder_name", "email", "instagram", "followers",
  "product_name", "category", "retail_price", "brand_story",
  "online_store", "fulfilment",
];

export default function StorefrontApply() {
  useEmbedResize();
  const [form, setForm] = useState(INITIAL);
  const [invalid, setInvalid] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const upd = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (invalid[k]) setInvalid((iv) => ({ ...iv, [k]: false }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const bad = {};
    REQUIRED.forEach((k) => { if (!String(form[k] || "").trim()) bad[k] = true; });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || "")) bad.email = true;
    if (Object.keys(bad).length) {
      setInvalid(bad);
      toast.error("Please fill in all required fields before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await api.submitCutApplication(form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong. Please email your entry directly to info@deluxxie.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="cut-apply-page">
      <StorefrontHeader />

      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-2">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#d0473e] font-semibold">
          <Sparkles size={12} /> The Cut · Applications open
        </div>
        <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight mt-3 leading-tight text-[#111]">
          Make the cut.
        </h1>
        <p className="text-base sm:text-lg text-[#6D7175] mt-4 leading-relaxed max-w-2xl">
          Free to enter. Every application reviewed within 48 hours. Only 8 brands per round — apply early.
        </p>
        <div className="mt-6 flex items-center gap-x-5 gap-y-2 text-xs text-[#6D7175] flex-wrap">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-[#d0473e]" /> Free to enter</span>
          <span className="inline-flex items-center gap-1.5"><Clock size={12} /> 48-hour review</span>
          <span className="inline-flex items-center gap-1.5"><Users size={12} /> 8 spots per round</span>
          <span className="inline-flex items-center gap-1.5"><CircleDollarSign size={12} /> Revenue share on sales only</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-8 pb-16">
        {submitted ? (
          <SuccessCard />
        ) : (
          <form onSubmit={submit} className="lp-card p-6 md:p-8" data-testid="cut-apply-form" noValidate>
            <Section title="Your brand" first>
              <Row>
                <Field id="brand_name" label="Brand name" required invalid={invalid.brand_name}>
                  <input className="lp-input" data-testid="cut-brand-name" value={form.brand_name} onChange={upd("brand_name")} placeholder="Your brand name" maxLength={100} />
                </Field>
                <Field id="founder_name" label="Your name" required invalid={invalid.founder_name}>
                  <input className="lp-input" data-testid="cut-founder-name" value={form.founder_name} onChange={upd("founder_name")} placeholder="Founder or contact name" maxLength={100} />
                </Field>
              </Row>
              <Row>
                <Field id="email" label="Email" required invalid={invalid.email}>
                  <input type="email" className="lp-input" data-testid="cut-email" value={form.email} onChange={upd("email")} placeholder="you@yourbrand.com" />
                </Field>
                <Field id="instagram" label="Instagram" required invalid={invalid.instagram}>
                  <input className="lp-input" data-testid="cut-instagram" value={form.instagram} onChange={upd("instagram")} placeholder="@yourbrand" maxLength={50} />
                </Field>
              </Row>
              <Row>
                <Field id="website" label="Website">
                  <input type="url" className="lp-input" data-testid="cut-website" value={form.website} onChange={upd("website")} placeholder="https://yourbrand.com" maxLength={200} />
                </Field>
                <Field id="followers" label="Instagram followers" required note="Approximate is fine" invalid={invalid.followers}>
                  <input className="lp-input" data-testid="cut-followers" value={form.followers} onChange={upd("followers")} placeholder="e.g. 12,400" maxLength={20} />
                </Field>
              </Row>
            </Section>

            <Section title="Your product">
              <Field id="product_name" label="Product entering The Cut" required invalid={invalid.product_name}>
                <input className="lp-input" data-testid="cut-product-name" value={form.product_name} onChange={upd("product_name")} placeholder="Product name — one line description" maxLength={200} />
              </Field>
              <Row>
                <Field id="category" label="Category" required invalid={invalid.category}>
                  <select className="lp-input" data-testid="cut-category" value={form.category} onChange={upd("category")}>
                    <option value="">Select category</option>
                    <optgroup label="Clothing">
                      <option value="clothing_dresses">Dresses</option>
                      <option value="clothing_sets">Sets + co-ords</option>
                      <option value="clothing_tops">Tops</option>
                      <option value="clothing_outerwear">Outerwear</option>
                      <option value="clothing_other">Other clothing</option>
                    </optgroup>
                    <optgroup label="Accessories">
                      <option value="acc_jewellery">Jewellery</option>
                      <option value="acc_bags">Bags</option>
                      <option value="acc_pins">Pins + badges</option>
                      <option value="acc_other">Other accessories</option>
                    </optgroup>
                    <option value="footwear">Footwear</option>
                    <option value="wearable_tech">Wearable tech</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field id="retail_price" label="Retail price (USD)" required invalid={invalid.retail_price}>
                  <input className="lp-input" data-testid="cut-retail-price" value={form.retail_price} onChange={upd("retail_price")} placeholder="$0.00" maxLength={20} />
                </Field>
              </Row>
            </Section>

            <Section title="Your story">
              <Field id="brand_story" label="Why should you make the cut?" required note="100–300 words recommended. Make it count." invalid={invalid.brand_story}>
                <textarea className="lp-input" data-testid="cut-brand-story" value={form.brand_story} onChange={upd("brand_story")}
                  placeholder="Tell us about your brand, your product, and why the Deluxxie community will vote for you. This becomes your brand card — voters see this. Write for the people who will decide."
                  maxLength={1000} rows={5} style={{ resize: "vertical" }} />
                <CharCount value={form.brand_story} max={1000} />
              </Field>
            </Section>

            <Section title="Fulfilment + store">
              <Field id="online_store" label="Do you have an online store?" required
                note="We integrate directly into our retail partner platforms. An online store makes this seamless — we can help if needed."
                invalid={invalid.online_store}>
                <select className="lp-input" data-testid="cut-online-store" value={form.online_store} onChange={upd("online_store")}>
                  <option value="">Select one</option>
                  <option value="shopify">Yes — Shopify</option>
                  <option value="other_platform">Yes — other platform</option>
                  <option value="wholesale_only">No — wholesale / agents only</option>
                  <option value="no_store">No — looking to set one up</option>
                </select>
              </Field>
              <Field id="fulfilment" label="How will you fulfil orders?" required invalid={invalid.fulfilment}>
                <select className="lp-input" data-testid="cut-fulfilment" value={form.fulfilment} onChange={upd("fulfilment")}>
                  <option value="">Select fulfilment method</option>
                  <option value="us_3pl">US-based warehouse / 3PL</option>
                  <option value="china_warehouse">China warehouse — ships internationally</option>
                  <option value="fashiongo">FashionGo / wholesale supplier ships direct</option>
                  <option value="alibaba">Alibaba supplier ships direct</option>
                  <option value="self_fulfil">Self-fulfil from studio or home</option>
                  <option value="need_support">Need fulfilment support</option>
                </select>
              </Field>
              <Field id="lead_time" label="Production / ship lead time"
                note="If you win, pre-orders open immediately. We set a fulfilment expectation for customers based on your lead time.">
                <select className="lp-input" data-testid="cut-lead-time" value={form.lead_time} onChange={upd("lead_time")}>
                  <option value="">Select lead time</option>
                  <option value="in_stock">In stock — ships within 7 days</option>
                  <option value="1_2_weeks">1–2 weeks</option>
                  <option value="3_4_weeks">3–4 weeks</option>
                  <option value="5_8_weeks">5–8 weeks</option>
                  <option value="over_8_weeks">Over 8 weeks</option>
                  <option value="unsure">Not sure yet</option>
                </select>
              </Field>
            </Section>

            <Section title="Promotion commitment">
              <Field id="promo_plan" label="How will you promote your entry to your audience?"
                note="Most votes wins. Brands that promote hard have the best chance. Be specific — this factors into our selection.">
                <textarea className="lp-input" data-testid="cut-promo-plan" value={form.promo_plan} onChange={upd("promo_plan")}
                  placeholder="e.g. 3 TikTok videos, daily Instagram stories, email to our list of 5,000, YouTube mention…"
                  maxLength={500} rows={3} style={{ resize: "vertical" }} />
                <CharCount value={form.promo_plan} max={500} />
              </Field>
              <Field id="anything_else" label="Anything else we should know?">
                <textarea className="lp-input" data-testid="cut-anything-else" value={form.anything_else} onChange={upd("anything_else")}
                  placeholder="Press, stockists, awards, partnerships, production capacity — anything that strengthens your entry."
                  maxLength={500} rows={3} style={{ resize: "vertical" }} />
              </Field>
            </Section>

            <button type="submit" disabled={submitting}
              className="lp-btn-primary w-full justify-center mt-6 py-3.5 text-sm font-medium"
              data-testid="cut-submit-btn">
              {submitting ? "Submitting…" : "Submit my entry →"}
            </button>

            <div className="text-[11px] text-[#8C9196] text-center mt-4 leading-relaxed">
              Free to enter · no exclusivity required · revenue share on sales only<br />
              By submitting you agree to promote your entry during the voting round if selected.<br />
              We review every application and respond within 48 hours.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, first, children }) => (
  <div className={first ? "" : "mt-8 pt-8 border-t border-gray-100"}>
    <div className="lp-section-label mb-4">{title}</div>
    <div className="space-y-4">{children}</div>
  </div>
);

const Row = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
);

const Field = ({ id, label, required, note, invalid, children }) => {
  const arr = React.Children.toArray(children);
  const control = arr[0];
  const rest = arr.slice(1);
  const cloned = React.isValidElement(control)
    ? React.cloneElement(control, {
        id,
        className: `${control.props.className || ""}${invalid ? " lp-input-invalid" : ""}`.trim(),
      })
    : control;
  return (
    <div>
      <label htmlFor={id} className="lp-label">
        {label}{required && <span className="text-[#d0473e] ml-0.5">*</span>}
      </label>
      {cloned}
      {rest}
      {note && <div className="lp-help">{note}</div>}
    </div>
  );
};

const CharCount = ({ value, max }) => {
  const n = (value || "").length;
  const near = n > max * 0.9;
  return (
    <div className={`text-[10px] text-right mt-1 tabular-nums ${near ? "text-[#d0473e]" : "text-gray-400"}`}>
      {n} / {max}
    </div>
  );
};

const SuccessCard = () => (
  <div className="lp-card p-10 text-center" data-testid="cut-apply-success">
    <div className="w-14 h-14 rounded-full bg-[#d0473e]/10 text-[#d0473e] flex items-center justify-center mx-auto mb-5">
      <CheckCircle2 size={28} />
    </div>
    <h2 className="text-2xl font-semibold tracking-tight text-[#111] mb-3">Entry received.</h2>
    <p className="text-sm text-[#6D7175] leading-relaxed max-w-md mx-auto">
      We review every application within 48 hours. Keep an eye on your inbox — we&apos;ll be in touch.
    </p>
    <p className="text-sm text-[#6D7175] leading-relaxed max-w-md mx-auto mt-4">
      In the meantime, start warming up your audience. If you make the round, you&apos;ll need them to vote.
    </p>
  </div>
);
